import { durableMediaWrites, rememberMediaResponse } from "../media/mediaReferences";
import axios from "axios";
import {
  clearCollectorToken,
  collectorAuthHeaders,
  getCollectorRefreshToken,
  getCollectorToken,
  setCollectorToken,
} from "../helpers/collectorSession";

const base = process.env.REACT_APP_SERVERURL || "";

const api = axios.create({
  baseURL: base,
  headers: { "Content-Type": "application/json" },
});

let renewing: Promise<boolean> | null = null;

/**
 * Renewal runs under a lock every tab shares, so a stalled call would hold up renewal in all of
 * them. A timeout is not a refusal: the session is kept for the next try.
 */
export const COLLECTOR_RENEW_TIMEOUT_MS = 15000;

/** Sign-out gives up after this long; the person is still signed out here and told the rest may not be. */
export const COLLECTOR_SIGN_OUT_TIMEOUT_MS = 15000;

/** Messages the backend sends when the session itself is no good (not a business error that is also a 401). */
const SESSION_ERRORS = ["Not authenticated", "SESSION_EXPIRED", "ACCOUNT_MERGED", "Collector account not found"];

export const isCollectorSessionError = (error: any): boolean =>
  error?.response?.status === 401 && SESSION_ERRORS.includes(error?.response?.data?.message);

/** Refresh answers that mean the token itself is no good. */
const REFUSED = [400, 401, 403];

/** What this tab renews with: the refresh token, or the access token for sessions saved before refresh tokens were kept. */
const renewalKey = (): string | null => getCollectorRefreshToken() || getCollectorToken();

/** Per tab: the legacy token (access token only) the server already refused to renew, as a short hash. */
const LEGACY_REFUSED_KEY = "hkp.collector.legacyRenewRefused";

/** A short hash, so the token itself is not copied into another store. */
const tokenMark = (token: string): string => {
  let h = 5381;
  for (let i = 0; i < token.length; i += 1) h = ((h * 33) ^ token.charCodeAt(i)) >>> 0;
  return `${token.length}:${h.toString(36)}`;
};

const legacyRenewRefused = (token: string): boolean => {
  try {
    return sessionStorage.getItem(LEGACY_REFUSED_KEY) === tokenMark(token);
  } catch {
    return false;
  }
};

const rememberLegacyRenewRefused = (token: string): void => {
  try {
    sessionStorage.setItem(LEGACY_REFUSED_KEY, tokenMark(token));
  } catch {
    // Storage blocked: the next page load just asks again.
  }
};

/**
 * Runs `work` under a browser-wide lock where the browser has one (Web Locks), so two tabs never
 * spend the same rotating refresh token at once. Without it, the per-tab single flight plus the
 * "another tab won" check below still recover.
 */
const withRenewLock = <T,>(work: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
  return locks?.request ? locks.request("hkp-collector-renew", work) : work();
};

/**
 * Swaps the stored session for a fresh pair (the server rotates the refresh token on every use),
 * so people who open My Collection rarely stay signed in.
 * One call per tab at a time. Resolves true when storage now holds a usable session (this tab
 * renewed it, or another tab did), false when the person has to sign in again. A session the
 * server refuses is cleared, so a dead token is not kept around.
 */
export const renewCollectorSession = (): Promise<boolean> => {
  const tried = renewalKey();
  if (!tried) return Promise.resolve(false);
  // A legacy session the server already refused to renew in this tab is not asked again on every page.
  if (!getCollectorRefreshToken() && legacyRenewRefused(tried)) return Promise.resolve(false);
  if (!renewing) {
    renewing = withRenewLock(async () => {
      // Another tab renewed (or signed out) while this one waited for the lock.
      const before = renewalKey();
      if (before !== tried) return Boolean(before);
      try {
        const res = await axios.post(
          `${base}/collector-auth/refresh`,
          { refreshToken: tried },
          { headers: { "Content-Type": "application/json" }, timeout: COLLECTOR_RENEW_TIMEOUT_MS }
        );
        const data = res.data?.data;
        const now = renewalKey();
        // Signed out, or another account or tab took over, while this was in flight: leave that alone.
        if (now !== tried) return Boolean(now);
        if (!data?.token) return false;
        setCollectorToken(data.token, data.refreshToken);
        return true;
      } catch (error: any) {
        const now = renewalKey();
        // Another tab rotated the token first, so ours was refused: use theirs.
        if (now && now !== tried) return true;
        // The server refused the refresh token (not a network or server fault): this session is dead, so drop it.
        // A session saved before refresh tokens were kept renews with its access token, which the server
        // refuses; that token may still work, so it is kept until a real request is refused with it.
        if (REFUSED.includes(error?.response?.status) && now === tried) {
          if (getCollectorRefreshToken()) clearCollectorToken("refused");
          else rememberLegacyRenewRefused(tried);
        }
        return false;
      }
    }).finally(() => {
      renewing = null;
    });
  }
  return renewing;
};

/**
 * For a call that failed with a session error while sending `sentToken`: true when the call is
 * worth retrying with the stored token. If storage already holds a newer token (another request
 * or tab renewed meanwhile) that is enough; otherwise renew.
 */
export const recoverCollectorSession = async (sentToken?: string | null): Promise<boolean> => {
  const stored = getCollectorToken();
  if (stored && sentToken && stored !== sentToken) return true;
  const renewed = await renewCollectorSession();
  // A legacy session (access token only) that a real request refused and that cannot renew is over.
  if (!renewed && !getCollectorRefreshToken() && sentToken && getCollectorToken() === sentToken) {
    clearCollectorToken("refused");
  }
  return renewed;
};

const sentCollectorToken = (config: any): string | null => {
  const headers = config?.headers;
  const value = headers?.get ? headers.get("X-HKP-Collector-Token") : headers?.["X-HKP-Collector-Token"];
  return value ? String(value) : null;
};

/**
 * Collector calls made outside the `api` instance (uploads, fetch) go through this: on a session
 * error it renews once and runs `call` again, which reads the fresh token from storage.
 */
export const withCollectorSession = async <T,>(call: () => Promise<T>, isSessionError = isCollectorSessionError): Promise<T> => {
  const sent = getCollectorToken();
  try {
    return await call();
  } catch (error) {
    if (isSessionError(error) && (await recoverCollectorSession(sent))) return call();
    throw error;
  }
};

api.interceptors.response.use(
  (response) => {
    rememberMediaResponse(response.data);
    return response;
  },
  async (error) => {
    const config = error?.config;
    const authCall = String(config?.url || "").startsWith("/collector-auth/");
    if (
      isCollectorSessionError(error) &&
      config &&
      !config._renewed &&
      !authCall &&
      (await recoverCollectorSession(sentCollectorToken(config)))
    ) {
      config._renewed = true;
      config.headers = config.headers || {};
      config.headers["X-HKP-Collector-Token"] = getCollectorToken();
      return api.request(config);
    }
    return Promise.reject(error);
  }
);

api.interceptors.request.use((config) => {
  if (config.data) config.data = durableMediaWrites(config.data);
  const token = getCollectorToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["X-HKP-Collector-Token"] = token;
  }
  return config;
});

/**
 * Ends this account's sessions on every device. Sends the refresh token too, so the server can
 * still end the session when the access token has already expired. Does not clear storage;
 * the caller does that either way and tells the person if the server call failed.
 */
export const signOutCollector = () =>
  api.post(
    "/collector-auth/sign-out",
    { refreshToken: getCollectorRefreshToken() || undefined },
    { timeout: COLLECTOR_SIGN_OUT_TIMEOUT_MS }
  );

export const sendCollectorOtp = (phone: string) =>
  api.post("/collector-auth/send-otp", { phone });

export const verifyCollectorOtp = (phone: string, otp: string, displayName?: string) =>
  api.post("/collector-auth/verify-otp", { phone, otp, displayName });

export const getCollectorMe = () => api.get("/collector/me");

export const getCollectorMasters = () => api.get("/collector/masters");

export const listCollectorItems = (params?: { status?: string; itemType?: string }) =>
  api.get("/collector/items", { params });

export const getCollectorItem = (itemId: string) =>
  api.get(`/collector/items/${itemId}`);

export const sessionFromCert = (productVerificationToken: string) =>
  api.post("/collector-auth/session-from-cert", { productVerificationToken });

export const recordCertScan = (productVerificationToken: string, productUniqueId: string) =>
  api.post("/collector-auth/record-cert-scan", { productVerificationToken, productUniqueId });

export const saveScannedItem = (productUniqueId: string) =>
  api.post("/collector/items/scanned", { productUniqueId });

export const createExternalItem = (body: Record<string, unknown>) =>
  api.post("/collector/items/external", body);

export const updateCollectorItem = (itemId: string, body: Record<string, unknown>) =>
  api.patch(`/collector/items/${itemId}`, body);

export const getItemQr = (itemId: string) =>
  api.get(`/collector/items/${itemId}/qr`);

export const getItemCoverBlob = (itemId: string) =>
  api.get(`/collector/items/${itemId}/cover`, {
    responseType: "blob",
    headers: { Accept: "image/*,application/octet-stream" },
  });

export const getPublicPiece = (collectorItemId: string) =>
  axios.get(`${base}/collector/piece/${collectorItemId}/public`).then(response => { rememberMediaResponse(response.data); return response; });

export const unlockPublicPiece = (collectorItemId: string, passkey: string) =>
  axios.post(`${base}/collector/piece/${collectorItemId}/unlock`, { passkey }).then(response => { rememberMediaResponse(response.data); return response; });

export const updateCollectorProfile = (body: {
  displayName?: string;
  email?: string;
  defaultQrVisibility?: string;
}) => api.patch("/collector/me", body);

export const setCollectorPasskey = (passkey: string, currentPasskey?: string) =>
  api.put("/collector/me/passkey", { passkey, currentPasskey });

export const requestPremiumAccess = (email: string, message?: string) =>
  api.post("/collector/premium-callback", { email, message });

export const listArchivedCollectorItems = () => api.get("/collector/items/archived");

export const archiveCollectorItem = (itemId: string) =>
  api.post(`/collector/items/${itemId}/archive`);

export const restoreCollectorItem = (itemId: string) =>
  api.post(`/collector/items/${itemId}/restore`);

export const getItemCertificate = (itemId: string) =>
  api.get(`/collector/items/${itemId}/certificate`);

export const requestItemAccess = (itemId: string) =>
  api.post(`/collector/items/${itemId}/access-request`);

export const getItemAccessRequest = (itemId: string) =>
  api.get(`/collector/items/${itemId}/access-request`);

/** Buyer says they bought this sold piece; the selling studio approves or rejects. */
export const createOwnershipClaim = (itemId: string, body: { note?: string; invoiceNumber?: string }) =>
  api.post(`/collector/items/${itemId}/ownership-claim`, body);

export const getOwnershipClaim = (itemId: string) =>
  api.get(`/collector/items/${itemId}/ownership-claim`);

/** Numbers this account has held, newest first. */
export const listCollectorPhones = () => api.get("/collector/me/phones");

export const sendPhoneChangeOtp = (phone: string) =>
  api.post("/collector/me/phone/send-otp", { phone });

/** Both numbers are proved: the new one by OTP, the current one by its OTP or the account passkey. */
export const verifyPhoneChange = (
  phone: string,
  otp: string,
  proof: { currentOtp?: string; passkey?: string },
) => api.post("/collector/me/phone/verify", { phone, otp, ...proof });

/** Merge another account (proved by an OTP to its number) into this one. */
export const sendMergeOtp = (phone: string) =>
  api.post("/collector/me/merge/send-otp", { phone });

export const verifyMerge = (phone: string, otp: string) =>
  api.post("/collector/me/merge/verify", { phone, otp });

export const listCollectorMasterTerms = (fieldKey: string) =>
  api.get("/collector/master-terms", { params: { fieldKey } });

export const patchCollectorMasterTerm = (
  id: string,
  body: { action: "HIDE" | "RESTORE" | "RENAME"; label?: string }
) => api.patch(`/collector/master-terms/${id}`, body);

export const mergeCollectorMasterTerm = (id: string, targetId: string) =>
  api.post(`/collector/master-terms/${id}/merge`, { targetId });

export const collectorDetailsAllowed = (accessState?: string) => {
  if (!accessState) return true;
  return accessState !== "ACCESS_LOCKED";
};

export { api as collectorApi, collectorAuthHeaders };

